<template>
  <div class="flex flex-column gap-1">
    <div class="bending-stress-chart" :class="{ 'is-static': isStatic }">
      <DDV
        v-slot="{ enableMouseTracking, onUpdateNearestPoint, margins }"
        v-bind="$attrs"
        class="bending-stress"
        :resize-throttle-ms="100"
        @update:dom-state="domState = $event"
      >
        <template v-if="domState">
          <rect
            v-if="domState.plotWidth > 0"
            :x="margins.left"
            :y="margins.top"
            :width="domState.plotWidth"
            :height="domState.plotHeight"
            class="plotBg"
            fill="var(--plotSurface)"
          />
          <template
            v-if="
              entryRows.length > 0 &&
              equipmentDepthsFt.length > 0 &&
              datasets[0]?.points?.length &&
              datasets[1]?.points?.length
            "
          >
            <!-- Deflection Axis -->
            <Axis
              label="Deflection (In)"
              :x="margins.left"
              :y="margins.top"
              :domain="deflectionDomain"
              :axis-tick-formatter="deflectionTickFormatter"
              :margin-px="6"
              :scaler="deflectionLinearScaler"
              :tick-frequency="ddvTickFrequencies?.y"
              is-vertical
              draw-grid-lines
            />

            <!-- Bending Stress Axis -->
            <Axis
              label="Bending Stress (psi)"
              :y="margins.top"
              :x="margins.left + domState.plotWidth"
              :domain="bendingStressDomain"
              :margin-px="6"
              :scaler="bendingStressLinearScaler"
              :tick-frequency="ddvTickFrequencies?.y"
              is-right
              is-vertical
            />

            <!-- Equipment String Length Axis -->
            <Axis
              draw-grid-lines
              label="Equipment String Length (ft)"
              :x="margins.left"
              :y="margins.top"
              :domain="equipmentStringLengthDomain"
              :scaler="equipmentStringLengthScaler"
              :tick-frequency="ddvTickFrequencies?.x || '20px'"
            />

            <!-- Deflection & Bending Stress lines -->
            <g
              v-for="{ dataset, index } in datasetsInDrawOrder"
              :key="index"
              :transform="`translate(${margins.left} ${margins.top})`"
            >
              <BSP
                v-slot="{ nearestPoint, screenPoints }"
                ignore-y-values
                :ignore-x-values="!!disableTracking"
                :points="dataset.points"
                :screen-points="dataset.screenPoints"
                :track-nearest-point="
                  enableMouseTracking && activeDataSets[index]
                "
                @update:nearest-point="
                  onUpdateNearestPoint(
                    dataset?.title ?? '',
                    index,
                    activeDataSets[index],
                    $event,
                    dataset.xValueFormatter,
                    dataset.yValueFormatter,
                    dataset.xAxisLabel,
                    dataset.yAxisLabel
                  )
                "
              >
                <LinePlot
                  :is-active="activeDataSets[index]"
                  :data-series-number="index"
                  :screen-points="screenPoints"
                  curve-interpolator="straight"
                />
                <NearestPoint
                  v-if="nearestPoint?.node"
                  :data-series-number="index"
                  :nearest-point="nearestPoint"
                />
              </BSP>
            </g>

            <!-- Equipment Lines -->
            <g
              v-if="showEquipments"
              class="equipment-lines"
              :transform="`translate(${margins.left} ${margins.top})`"
            >
              <BSP
                v-for="(ds, i) in equipmentDepthsFt"
                v-slot="{ screenPoints }"
                :key="`equipment-line-${i}`"
                :screen-points="[
                  {
                    x: getEquipmentMarkerStringLengthFt(
                      ds.startingStringLengthFt
                    ),
                    y: 0
                  },
                  {
                    x: getEquipmentMarkerStringLengthFt(
                      ds.startingStringLengthFt
                    ),
                    y: maxHeight
                  }
                ]"
                :points="[
                  {
                    x: getEquipmentMarkerStringLengthFt(
                      ds.startingStringLengthFt
                    ),
                    y: 0
                  },
                  {
                    x: getEquipmentMarkerStringLengthFt(
                      ds.startingStringLengthFt
                    ),
                    y: maxHeight
                  }
                ]"
              >
                <g :class="`equipment-line equipment-line-${ds.type}`">
                  <text v-if="!refreshingText" class="equipment-text">
                    <textPath
                      startOffset="20%"
                      :href="`#equipment-length-line-${placement}-${ds.type}-${i}`"
                    >
                      {{ ds.type }}
                    </textPath>
                  </text>
                  <LinePlot
                    :id="`equipment-length-line-${placement}-${ds.type}-${i}`"
                    :title="ds.type + ' Length'"
                    class="equipment-line-plot"
                    :screen-points="
                      screenPoints.map(p => {
                        return {
                          y: p.y,
                          x: equipmentStringLengthScaler.scale(p.x)
                        };
                      })
                    "
                    curve-interpolator="straight"
                  />
                </g>
              </BSP>
            </g>
          </template>
          <template v-else>
            <text
              fill="var(--plotColor0)"
              x="50%"
              y="50%"
              dominant-baseline="middle"
              text-anchor="middle"
            >
              No Data
            </text>
          </template>

          <ChartTitle
            v-if="!hideTitle"
            :title="BENDING_STRESS_PLACEMENT_TITLES[placement]"
          />

          <Crosshairs
            v-if="!isStatic"
            ignore-y
            :x="margins.left"
            :y="margins.top"
          />
        </template>
      </DDV>

      <div>
        <CustomLegend
          v-if="drawLegend"
          v-model="activeDataSets"
          :horizontal="true"
          :model="datasets"
        >
          <template v-if="!disableEquipmentLegend" #footer>
            <LabelledToggle
              v-model="showEquipments"
              text="Show Equipment Lengths"
            />
          </template>
        </CustomLegend>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import {
  Axis,
  BSP,
  DDV,
  Crosshairs,
  LinePlot,
  ChartTitle,
  NearestPoint
} from "@incytestudios/ddv";
import { injectStrict } from "@/utils/utils";
import {
  scenarioKey,
  scenarioCalculationsKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";

import type { Scenario } from "@/composables/forms/formTypes";
import type {
  DeviationSurveyEntryRows,
  ScenarioCalculations
} from "@/composables/scenarioCalculations/calculationTypes";
import CustomLegend from "./CustomLegend.vue";
import LabelledToggle from "@/components/LabelledToggle.vue";
import {
  BENDING_STRESS_PLACEMENT_TITLES,
  getEquipmentMarkerStringLengthFt,
  useDeviationBendingStressChartData,
  type BendingStressPlacement
} from "@/libs/charting/deviationBendingStress/useDeviationBendingStressChartData";
const props = defineProps<{
  hideTitle?: boolean;
  disableEquipmentLegend?: boolean;
  disableTracking?: boolean;
  isStatic?: boolean;
  scenario?: Scenario | null;
  scenarioCalculations?: ScenarioCalculations;
  entryRows: DeviationSurveyEntryRows["data"];
  drawLegend: boolean;
  drawEquipmentLabels: boolean;
  placement: BendingStressPlacement;
}>();
const scenario = props.scenario
  ? ref<Scenario>(props.scenario)
  : injectStrict(scenarioKey);
const calcs =
  props.scenarioCalculations || injectStrict(scenarioCalculationsKey);
const {
  domState,
  ddvTickFrequencies,
  refreshingText,
  equipmentDepthsFt,
  deflectionDomain,
  deflectionTickFormatter,
  bendingStressDomain,
  equipmentStringLengthDomain,
  datasets,
  equipmentStringLengthScaler,
  deflectionLinearScaler,
  bendingStressLinearScaler,
  maxHeight
} = useDeviationBendingStressChartData({
  scenarioCalculations: calcs,
  scenario: scenario.value,
  entryRows: () => props.entryRows,
  placement: () => props.placement
});

const activeDataSets = ref(datasets.value.map(() => true));
const showEquipments = ref(props.drawEquipmentLabels);
// SVG paints in document order. Draw the bending stress series (index 1) last,
// so that its line is in front. The index still sets the colour and the legend.
const datasetsInDrawOrder = computed(() =>
  datasets.value
    .map((dataset, index) => ({ dataset, index }))
    .sort((a, b) => Number(a.index === 1) - Number(b.index === 1))
);
</script>

<style lang="scss" scoped>
.disabled {
  :deep(.ddv) {
    &.isPlotHovered {
      cursor: auto !important;
    }
  }
}
.bending-stress-chart {
  * {
    transition: all 0s !important;
  }
  :deep(.isVertical.axis:not(.isRight) .gridlines g.zero) {
    display: none;
  }
  // DDV clips each axis to the plot height, which cuts the tick labels on the
  // top and bottom edges. The clip is an inline style, so it needs !important.
  :deep(svg.axis.isVertical > g) {
    clip-path: none !important;
  }

  &.is-static {
    :deep(.ddv) {
      &.isPlotHovered {
        cursor: auto !important;
      }
    }
  }

  svg:first-of-type {
    :deep(.tick.isVertical.last) {
      text {
        // if we remove y padding from the deflection domain's max, the axis should start at 0, and we need this translation to prevent the first tick from being cut off
        // transform: translateY(6px);
      }
      g.zero {
        visibility: hidden;
      }
    }
  }

  :deep(.tick.isVertical) {
    text {
      font-size: var(--axisTickFontSize) !important;
      font-weight: var(--axisTickFontWeight) !important;
    }
  }
  .equipment-text {
    font-family: var(--mainFontFamily);
    font-weight: normal;
    font-size: 12px;
    transform: translate(-8px) rotate(-165deg);
  }
  .equipment-line {
    transition: none;
    opacity: 0.7;

    &.equipment-line-pump {
      fill: var(--plotColor3);
      .equipment-line-plot {
        stroke: var(--plotColor3);
      }
      &:first-of-type {
        .equipment-text {
          transform: translate(20px) rotate(-165deg);
        }
        .equipment-line-plot {
          opacity: 0.6;
          stroke: var(--plotColor3);
          stroke-width: 6px;
          transform: translate(3px);
        }
      }
    }

    &.equipment-line-intake {
      fill: var(--plotColor4);
      .equipment-line-plot {
        stroke: var(--plotColor4);
      }
    }
    &.equipment-line-seal {
      fill: var(--plotColor5);

      .equipment-line-plot {
        stroke: var(--plotColor5);
      }
    }
    &.equipment-line-motor {
      fill: var(--plotColor6);

      .equipment-line-plot {
        stroke: var(--plotColor6);
      }
    }
    &.equipment-line-sensor {
      fill: var(--plotColor7);

      .equipment-line-plot {
        stroke: var(--plotColor7);
      }
    }
  }
  :deep(.additional-plot) {
    .additional-line {
      transition: none;
      stroke: var(--additionalPlotColor);
    }

    .additional-point {
      --activePlotColor: var(--additionalPlotColor);
    }
  }
}
</style>
